import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { Badge, BlockStack, Box, InlineStack, Link as PolarisLink, Text } from '@shopify/polaris';

import { useSession } from '@/providers/session';

/**
 * The navigation for standalone mode.
 *
 * ── Why the app has to draw its own ────────────────────────────────────────
 *
 * Embedded, `<NavMenu>` renders `<ui-nav-menu>`, which App Bridge lifts OUT of
 * the iframe and draws in the Shopify admin's own sidebar. Standalone has no
 * admin sidebar and no App Bridge — the custom element is a polyfilled no-op and
 * the app would have no navigation at all.
 *
 * ⚠ And the failure is worse than "no nav": an undefined custom element renders
 * its CHILDREN INLINE, so without the polyfill hiding it you get a row of raw
 * links at the top of the page that looks like a stylesheet failed to load.
 *
 * ── The badge is not decoration ────────────────────────────────────────────
 *
 * This is a support session looking at a merchant's real settings, where a save
 * writes to their real store. Whoever is looking should never be in any doubt
 * about which of those they are doing.
 *
 * ── Nor is the identity line, and it was missing ───────────────────────────
 *
 * The badge answers "is this real?" and nothing answered "WHOSE?". Embedded,
 * the admin's own chrome is wrapped around the iframe and names the store on
 * every screen; standalone has no chrome, so an operator hopping between shops
 * from the ops dashboard had two tabs that were pixel-identical and wrote to
 * different merchants. So the second row carries the myshopify domain, the
 * numeric shop id, and a way out to the storefront.
 *
 * ⚠ The domain and the id are merchant DATA and are NOT translated — the same
 * rule as the country and language names in Advanced settings. Only the words
 * around them go through `t`, and `nav.shopId` interpolates rather than
 * concatenating a label with a number, because half of these languages put the
 * label on the other side.
 */

/**
 * The same four destinations `AppNavMenu` offers, plus Home — which is the root
 * marker there and has to be a real item here, because standalone mode has no
 * app title to click.
 */
const ITEMS = [
  { to: '/', key: 'nav.home' },
  { to: '/schedule', key: 'nav.schedule' },
  { to: '/settings', key: 'nav.settings' },
  { to: '/plans', key: 'nav.plans' },
] as const;

export function StandaloneNav() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const session = useSession();

  // `shop` is the myshopify domain, which is the ONE name that identifies a
  // store uniquely — a custom storefront domain does not map back to it, and a
  // shop's display name is not unique at all. `shopId` may legitimately be
  // absent (an old row, or a /verify whose shop-info read failed soft), so it
  // is rendered only when there is one rather than as an empty label.
  const shopDomain = session?.shop ?? '';
  const shopId = session?.shopId ?? null;

  return (
    <Box
      background="bg-surface"
      borderBlockEndWidth="025"
      borderColor="border"
      paddingInline="400"
      paddingBlock="300"
    >
      <BlockStack gap="150">
        <InlineStack align="space-between" blockAlign="center" gap="400">
          <InlineStack gap="400" blockAlign="center" wrap={false}>
            <Text as="span" variant="headingMd">
              Confetto{/* i18n-exempt — the brand. It is the same word in all 20 locales. */}
            </Text>
            <InlineStack gap="300" wrap>
              {ITEMS.map((i) => {
                const active = i.to === '/' ? pathname === '/' : pathname.startsWith(i.to);
                return (
                  <Link key={i.to} to={i.to} style={{ textDecoration: 'none' }}>
                    <Text
                      as="span"
                      tone={active ? undefined : 'subdued'}
                      fontWeight={active ? 'semibold' : 'regular'}
                    >
                      {/*
                        ⚠ No `defaultValue` here, and it is not an omission.
                        `initI18n` sets `parseMissingKeyHandler: (key) => key`, which
                        OVERRIDES defaultValue — a missing key renders as the key,
                        deliberately, so it is visible rather than blank. This line
                        used to pass `{ defaultValue: 'Home' }` and it never once
                        took effect: `nav.home` was undefined and every merchant in
                        standalone mode read the literal string "nav.home" in the
                        navigation bar. The fix is the key existing, not a fallback
                        that the app's own handler outranks.
                      */}
                      {t(i.key)}
                    </Text>
                  </Link>
                );
              })}
            </InlineStack>
          </InlineStack>
          <Badge tone="warning">{t('app.supportSession')}</Badge>
        </InlineStack>

        {/*
          Which shop is open — the line the bar was missing.

          ⚠ It WRAPS and it BREAKS WORDS, and both are load-bearing at 375 px.
          The smoke harness measures `document.scrollingElement.scrollWidth`
          against the viewport, and a myshopify handle can be sixty characters
          long. `truncate` needs a width to truncate against, which a flex child
          of unconstrained width does not have, so it would quietly become
          "overflow the page" instead of an ellipsis. Wrapping costs a second
          line on a phone and cannot push anything sideways.
        */}
        {shopDomain && (
          <InlineStack gap="200" blockAlign="center" wrap>
            <Text as="span" variant="bodySm" tone="subdued" breakWord>
              {shopDomain}
            </Text>
            {shopId && (
              <Text as="span" variant="bodySm" tone="subdued">
                {t('nav.shopId', { id: shopId })}
              </Text>
            )}
            <Text as="span" variant="bodySm">
              {/*
                ⚠ The one link in this bar that is MEANT to leave the SPA, and
                the only one that may be a real `href`. Polaris turns
                `target="_blank"` into `rel="noopener noreferrer"`, so the
                storefront tab gets no `window.opener` handle back into a page
                that is holding an impersonation token, and sends no referrer
                carrying this origin. The `<Text>` wrapper is what keeps it the
                size of the two lines beside it — Polaris `Link` sets colour and
                underline and inherits everything else.
              */}
              <PolarisLink url={`https://${shopDomain}`} target="_blank">
                {t('nav.openStorefront')}
              </PolarisLink>
            </Text>
          </InlineStack>
        )}
      </BlockStack>
    </Box>
  );
}
